'use client';

/** Right pane of the skills picker: the selected skill, and the attach action. */

import React from 'react';
import type { MeridianSkillPickerItem } from './types.js';

export interface SkillDetailProps {
  skill: MeridianSkillPickerItem | null;
  onAttach: () => void;
}

export function SkillDetail({ skill, onAttach }: SkillDetailProps) {
  if (!skill) {
    return (
      <div className="border border-border rounded-md p-3 overflow-y-auto max-h-[22rem] flex flex-col gap-2">
        <p className="text-sm text-muted-foreground">Select a skill to see details.</p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-md p-3 overflow-y-auto max-h-[22rem] flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground break-all">{skill.id}</h3>
        {skill.category && (
          <span className="shrink-0 text-xs text-muted-foreground border border-border rounded px-1.5 py-0.5">
            {skill.category}
          </span>
        )}
      </div>
      <p className="text-sm text-foreground whitespace-pre-wrap">{skill.description}</p>
      {skill.argumentHint && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Hint: </span>
          {skill.argumentHint}
        </p>
      )}
      {skill.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {skill.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      <dl className="text-xs text-muted-foreground grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 mt-1">
        <dt>Availability</dt>
        <dd className="text-foreground">{skill.availability}</dd>
        <dt>HITL</dt>
        <dd className="text-foreground">{skill.hitlRequired ? 'Required' : 'Not required'}</dd>
        <dt>Playbook size</dt>
        <dd className="text-foreground">~{skill.bodyChars.toLocaleString()} chars</dd>
      </dl>
      {!skill.canActivateInMeridian && (
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
          Not activatable in Meridian. {skill.howToGet}
        </p>
      )}
      <div className="mt-auto pt-2">
        <button
          type="button"
          disabled={!skill.canActivateInMeridian}
          onClick={onAttach}
          className="w-full min-h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
        >
          Attach skill
        </button>
      </div>
    </div>
  );
}
